import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import WhatsAppFloat from "@/components/WhatsAppFloat";
import { useLang } from "@/context/LanguageContext";


import { ArrowLeft, MapPin, Clock, Wind, CheckCircle, ExternalLink } from "lucide-react";
import Gallery from "@/components/Gallery";


export default function Rivas() {
  const { setLang } = useLang();

  useEffect(() => {
    setLang("es"); // Force Spanish on this specific page on mount
    document.title = "Vuelo Tándem en Parapente en Rivas | Pérez Zeledón Pura Vida";
    window.scrollTo(0, 0);
    
    // Meta Description Setup
    let metaDescription = document.querySelector('meta[name="description"]');
    if (!metaDescription) {
      metaDescription = document.createElement("meta");
      metaDescription.setAttribute("name", "description");
      document.head.appendChild(metaDescription);
    }
    metaDescription.setAttribute(
      "content", 
      "Reserve su vuelo tándem en parapente en Rivas de Pérez Zeledon. Anímese a volar con nuestros rides Clásico, de Aniversario o Adrenalina con vistas chivísimas del Valle del General en Pérez Zeledón."
    );

    // Remove any previous JSON-LD schema to ensure only the current one stays
    document.querySelectorAll('script[type="application/ld+json"]').forEach(script => {
      script.remove();
    });

    // SEO Schema Setup (JSON-LD) with 3 Rivas products
    const schemaData = {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "LocalBusiness",
          "@id": "https://paraglidetour.com/rivas",
          "name": "Parapente Perez Zeledon",
          "telephone": "+50683455135",
          "url": "https://www.parapenteperezzeledon.com/",
          "image": "https://ik.imagekit.io/paraglidetour/parapente-perez-zeledon-tandem-costa-rica-puravda-014.jpeg",
          "sameAs": [
            "https://www.instagram.com/paragliding_costa_rica/",
            "https://www.instagram.com/paraglidingeveryday/",
            "https://www.tiktok.com/@tandemparaglidecr/"
          ],
          "priceRange": "$$",
          "address": {
            "@type": "PostalAddress",
            "addressLocality": "Rivas, Perez Zeledon",
            "addressRegion": "San Jose",
            "addressCountry": "CR"
          }
        },
        // Product 1: Classic Rivas Flight
        {
          "@type": "Product",
          "name": "Vuelo Clásico en Parapente en Rivas",
          "description": "El despegue clásico de montaña en Pérez Zeledón. Un ride increíble con vista completa del valle y las montañas. ¡A cachete!",
          "url": "https://paraglidetour.com/tours/classic_rivas",
          "image": "https://ik.imagekit.io/paraglidetour/parapente-perez-zeledon-tandem-costa-rica-puravda-014.jpeg?updatedAt=1783540164762",
          "provider": {
            "@id": "https://paraglidetour.com/rivas/",
            "name": "Parapente Perez Zeledon",
            "sameAs": "https://www.parapenteperezzeledon.com/"
          },
          "offers": {
            "@type": "Offer",
            "priceCurrency": "CRC",
            "price": "60000.00",
            "availability": "https://schema.org/InStock",
            "url": "https://paraglidetour.com/tours/classic_rivas",
            "validFrom": "2026-01-01T08:00:00+00:00",
            "priceValidUntil": "2026-12-30T23:59:59+00:00",
            "priceSpecification": {
              "@type": "UnitPriceSpecification",
              "priceType": "https://schema.org/StrikethroughPrice",
              "price": "65000.00",
              "priceCurrency":  "CRC",
            },
          }
        },
        // Product 2: Anniversary Flight
        {
          "@type": "Product",
          "name": "Vuelo de Aniversario en Rivas",
          "description": "Celebre su día especial en las nubes. Incluye fotos y video tuanis para inmortalizar su aniversario volando sobre el valle de Rivas.",
          "url": "https://paraglidetour.com/tours/anniversary_rivas",
          "image": "https://ik.imagekit.io/paraglidetour/parapente-perez-zeledon-tandem-costa-rica-puravda-014.jpeg?updatedAt=1783540164762",
          "provider": {
            "@id": "https://paraglidetour.com/rivas/",
            "name": "Parapente Perez Zeledon",
            "sameAs": "https://www.parapenteperezzeledon.com/"
          },
          "offers": {
            "@type": "Offer",
            "priceCurrency": "CRC",
            "price": "65000.00",
            "availability": "https://schema.org/InStock",
            "url": "https://paraglidetour.com/tours/anniversary_rivas",
            "validFrom": "2026-01-01T08:00:00+00:00",
            "priceValidUntil": "2026-12-30T23:59:59+00:00",
            "priceSpecification": {
              "@type": "UnitPriceSpecification",
              "priceType": "https://schema.org/StrikethroughPrice",
              "price": "70000.00",
              "priceCurrency":  "CRC",
            },
          }
        },
        // Product 3: Adrenaline Flight
        {
          "@type": "Product",
          "name": "Vuelo Adrenalina en Parapente en Rivas",
          "description": "Para los más mandados. Sienta la adrenalina con maniobras acrobáticas pesadas para una aventura extrema sobre Pérez Zeledón.",
          "url": "https://paraglidetour.com/tours/adrenaline_rivas",
          "image": "https://ik.imagekit.io/paraglidetour/parapente-perez-zeledon-tandem-costa-rica-puravda-014.jpeg?updatedAt=1783540164762",
          "provider": {
            "@id": "https://paraglidetour.com/rivas/",
            "name": "Parapente Perez Zeledon",
            "sameAs": "https://www.parapenteperezzeledon.com/"
          },
          "offers": {
            "@type": "Offer",
            "priceCurrency": "CRC",
            "price": "70000.00",
            "availability": "https://schema.org/InStock",
            "url": "https://paraglidetour.com/tours/adrenaline_rivas",
            "validFrom": "2026-01-01T08:00:00+00:00",
            "priceValidUntil": "2026-12-30T23:59:59+00:00",
            "priceSpecification": {
              "@type": "UnitPriceSpecification",
              "priceType": "https://schema.org/StrikethroughPrice",
              "price": "75000.00",
              "priceCurrency":  "CRC",
            },
          }
        },
        {
          "@type": "FAQPage",
          "mainEntity": [
             {
                "@type": "Question",
                "name": "¿Ocupo experiencia para volar en parapente tándem?",
                "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "Nombres, para nada. En un vuelo tándem usted nada más va al suave disfrutando de la vista mientras el piloto certificado se encarga de todo. Volamos con gente desde los 5 hasta los 85 años."
                }
             },
             {
                "@type": "Question",
                "name": "¿Hay límite de peso para hacer el vuelo?",
                "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "El peso máximo del pasajero es de 135 kg (297 lbs). Ocupamos un IMC menor a 33 por pura seguridad."
                }
             },
             {
                "@type": "Question",
                "name": "¿Cuánto dura el ride en parapente?",
                "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "Los vuelos normales duran entre 20 y 30 minutos. Los vuelos de distancia y en la Cola de Ballena pueden durar hasta 45 o 60 minutos."
                }
             },
             {
                "@type": "Question",
                "name": "¿Qué pasa si el clima se pone feo?",
                "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "La seguridad es primero. Si las condiciones no prometen, reprogramamos el vuelo de gratis o le devolvemos la plata del depósito."
                }
             },
             {
                "@type": "Question",
                "name": "¿Dan fotos y video del vuelo?",
                "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "¡Claro mae! Tenemos paquetes con cámaras GoPro e Insta360 en todos los vuelos. Tomas con dron a pedido. Si está cumpliendo años, el video de GoPro le sale gratis."
                }
             },
             {
                "@type": "Question",
                "name": "¿Puedo pagar en efectivo o con crypto?",
                "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "El depósito se hace en línea con tarjeta (Stripe). El resto lo puede pagar en el despegue en efectivo (Dólares o Colones), tarjeta, o Bitcoin."
                }
             }
          ]
        }
      ]
    };

    const scriptSchema = document.createElement('script');
    scriptSchema.setAttribute('id', 'seo-schema');
    scriptSchema.setAttribute('type', 'application/ld+json');
    scriptSchema.textContent = JSON.stringify(schemaData);
    document.head.appendChild(scriptSchema);

    return () => {
      if (scriptSchema && scriptSchema.parentNode) {
        scriptSchema.parentNode.removeChild(scriptSchema);
      }
    };
  }, [setLang]);

  return (
    <div className="min-h-screen bg-sand text-jungle">
      
      <Header />

      {/* SEO Intro Section with Background Image */}
      <section 
        className="pt-20 pb-16 relative bg-cover bg-center border-b border-jungle/10"
        style={{ backgroundImage: `url('https://ik.imagekit.io/paraglidetour/the_best_paragliding_tour/parapente_perez_zeledon_pura_vida.webp')` }}
      >
        {/* Changed background overlay to light to ensure black text is readable */}
        <div className="absolute inset-0 bg-white/0 backdrop-blur-[0px]"></div>

        <div className="max-w-5xl mx-auto px-6 md:px-12 text-center relative z-10">
          <Link
            to="/"
            className="inline-flex items-center gap-2 font-overline text-[10px] text-white hover:text-zinc-700 transition-colors mb-6 font-semibold"
          >
            <ArrowLeft size={14} /> VOLVER A LA PÁGINA PRINCIPAL
          </Link>
          <div className="font-overline text-white text-xs mb-3 font-bold tracking-wider">Aventura en Costa Rica</div>
          
          {/* Changed headings to black */}
          <h1 className="font-display text-4xl md:text-5xl text-white font-bold leading-tight mb-6">
            Parapente Tándem en Rivas
          </h1>
          <p className="text-white text-base md:text-lg max-w-3xl mx-auto leading-relaxed font-medium">
            Descubra la emoción de <strong>volar en parapente en Costa Rica</strong>. Nuestros vuelos tándem son la experiencia definitiva para los que buscan adrenalina y conectar de verdad con la naturaleza en <strong>Pérez Zeledón</strong>. Mándese a volar por los cielos de <strong>Rivas</strong> y disfrute de una vista panorámica chivísima sobre las montañas y los valles verdes. ¡Pura vida!
          </p>
        </div>
      </section>

      {/* Flight Packages Section (3 Options for Rivas) */}
      <section className="pt-16 md:pt-24 pb-16 bg-sand">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-12">
            <h2 className="font-display text-3xl md:text-4xl text-jungle mb-4">
              Escoja su Ride en Rivas
            </h2>
            <p className="text-forest text-lg">
              Opciones exclusivas despegando desde los mejores puntos de montaña en Pérez Zeledón.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {/* Classic Flight */}
            <article className="bg-white rounded-2xl border border-jungle/15 shadow-sm hover:shadow-lg transition-shadow relative overflow-hidden flex flex-col">
              <img 
                src="https://ik.imagekit.io/paraglidetour/parapente-perez-zeledon-tandem-costa-rica-puravda-019.jpeg?updatedAt=1783540170527" 
                alt="Vuelo Clásico en Montañas de Rivas" 
                className="w-full h-48 object-cover"
              />
              <div className="p-6 md:p-8 flex flex-col flex-grow">
                <div className="flex items-center gap-3 mb-4 text-terracotta">
                  <MapPin size={24} />
                  <h3 className="font-display text-2xl text-jungle">Vuelo Clásico</h3>
                </div>
                <p className="text-forest mb-6 flex-grow">
                  El despegue clásico de montaña en Pérez Zeledón. Un ride increíble con vista completa del valle y la naturaleza. Todo al suave.
                </p>
                
                <ul className="space-y-3 mb-8">
                  <li className="flex items-center gap-2 text-forest">
                    <Clock size={18} className="text-terracotta" />
                    <span>Duración del vuelo: <strong>15 a 20 minutos</strong></span>
                  </li>
                  <li className="flex items-center gap-2 text-forest">
                    <Wind size={18} className="text-terracotta" />
                    <span>Vuelo térmico de montaña</span>
                  </li>
                  <li className="flex items-center gap-2 text-forest">
                    <CheckCircle size={18} className="text-jungle" />
                    <span>Instrucción incluida</span>
                  </li>
                </ul>

                 <div className="flex flex-col gap-2 items-start">
                                      <Link to="/tandem/rivas_mountain" data-testid="package-details-btn-rivas_mountain" className="btn-primary text-center justify-center font-overline text-[20px] text-center justify-center text-terracotta hover:text-forest transition-colors">
                                        Detalles →
                                      </Link>
                                     
                              </div>

                <div className="border-t border-jungle/10 pt-6 mt-auto">
                  <div className="flex flex-col mb-4">
                    <span className="line-through text-forest/70 text-sm font-medium">Antes 65000 colones </span>
                    <span className="font-display text-4xl text-jungle">55000<span className="text-xl"> colones</span></span>
                  </div>
                  <a href="https://wa.me/50683455135" className="btn-primary w-full text-center justify-center">
                    Agendar con SINPE
                     <img 
                      src="https://ik.imagekit.io/paraglidetour/whatsapp-50683455135-parapente-puravida.svg" 
                      alt="WhatsApp Icon"
                      className="inline-block ml-2 w-5 h-5"
                    ></img>
                  </a>
                </div>
              </div>
            </article>

            {/* Anniversary Flight */}
            <article className="bg-white rounded-2xl border border-jungle/15 shadow-sm hover:shadow-lg transition-shadow relative overflow-hidden flex flex-col">
              <div className="absolute top-0 right-0 bg-terracotta text-white font-overline text-[10px] px-3 py-1 rounded-bl-lg z-10">
                FECHA ESPECIAL
              </div>             
              <img 
                src="https://ik.imagekit.io/paraglidetour/parapente-perez-zeledon-tandem-costa-rica-puravda-014.jpeg?updatedAt=1783540164762" 
                alt="Vuelo de Cumpleaños en Rivas" 
                className="w-full h-48 object-cover"
              />
              <div className="p-6 md:p-8 flex flex-col flex-grow">
                <div className="flex items-center gap-3 mb-4 text-terracotta">
                  <MapPin size={24} />
                  <h3 className="font-display text-2xl text-jungle">Vuelo Cumpleaños</h3>
                </div>
                <p className="text-forest mb-6 flex-grow">
                  Celebre su día especial en las nubes. Incluye un paquete tuanis de fotos y video para inmortalizar su aniversario volando sobre el valle de Rivas.
                </p>
                
                <ul className="space-y-3 mb-8">
                  <li className="flex items-center gap-2 text-forest">
                    <Clock size={18} className="text-terracotta" />
                    <span>Duración del vuelo: <strong>15 a 20 minutos</strong></span>
                  </li>
                  <li className="flex items-center gap-2 text-forest">
                    <Wind size={18} className="text-terracotta" />
                    <span>Fotos y Videos incluidos</span>
                  </li>
                  <li className="flex items-center gap-2 text-forest">
                    <CheckCircle size={18} className="text-jungle" />
                    <span>Instrucción incluida</span>
                  </li>
                </ul>
           <div className="flex flex-col gap-2 items-start">
                                <Link to="/tandem/birthday" data-testid="package-details-btn-birthday" className="btn-primary text-center justify-center font-overline text-[20px] text-center justify-center text-terracotta hover:text-forest transition-colors">
                                  Detalles →
                                </Link>
                               
                        </div>


                <div className="border-t border-jungle/10 pt-6 mt-auto">
                  <div className="flex flex-col mb-4">
                    <span className="line-through text-forest/70 text-sm font-medium">Antes 70000 colones</span>
                    <span className="font-display text-4xl text-jungle">60000<span className="text-xl">colones</span></span>
                  </div>
                  <a href="https://wa.me/50683455135" className="btn-primary w-full text-center justify-center">
                    Agendar con SINPE
                     <img 
                      src="https://ik.imagekit.io/paraglidetour/whatsapp-50683455135-parapente-puravida.svg" 
                      alt="WhatsApp Icon"
                      className="inline-block ml-2 w-5 h-5"
                    ></img>
                  </a>
                </div>
              </div>
            </article>

            {/* Adrenaline Flight */}
            <article className="bg-white rounded-2xl border border-jungle/15 shadow-sm hover:shadow-lg transition-shadow relative overflow-hidden flex flex-col">
              <div className="absolute top-0 right-0 bg-jungle text-white font-overline text-[10px] px-3 py-1 rounded-bl-lg z-10">
                EXTREMO
              </div>
              <img 
                src="https://ik.imagekit.io/paraglidetour/parapente-perez-zeledon-tandem-costa-rica-puravda-003.jpeg?updatedAt=1783540168521" 
                alt="Vuelo Adrenalina en Rivas" 
                className="w-full h-48 object-cover"
              />
              <div className="p-6 md:p-8 flex flex-col flex-grow">
                <div className="flex items-center gap-3 mb-4 text-terracotta">
                  <MapPin size={24} />
                  <h3 className="font-display text-2xl text-jungle">Vuelo Adrenalina</h3>
                </div>
                <p className="text-forest mb-6 flex-grow">
                  Para los más mandados. Sienta la adrenalina al tope con maniobras acrobáticas fuertes (wingovers y espirales) para un ride extremo sobre Pérez Zeledón.
                </p>
                
                <ul className="space-y-3 mb-8">
                  <li className="flex items-center gap-2 text-forest">
                    <Clock size={18} className="text-terracotta" />
                    <span>Duración del vuelo: <strong>20 a 30 minutos</strong></span>
                  </li>
                  <li className="flex items-center gap-2 text-forest">
                    <Wind size={18} className="text-terracotta" />
                    <span>Maniobras acrobáticas incluidas</span>
                  </li>
                  <li className="flex items-center gap-2 text-forest">
                    <CheckCircle size={18} className="text-jungle" />
                    <span>Instrucción incluida</span>
                  </li>
                </ul>

                 <div className="flex flex-col gap-2 items-start">
                                      <Link to="/tandem/adrenaline" data-testid="package-details-btn-adrenaline" className="btn-primary text-center justify-center font-overline text-[20px] text-center justify-center text-terracotta hover:text-forest transition-colors">
                                        Detalles →
                                      </Link>
                                     
                              </div>

                <div className="border-t border-jungle/10 pt-6 mt-auto">
                  <div className="flex flex-col mb-4">
                    <span className="line-through text-forest/70 text-sm font-medium">Antes 75000 colones</span>
                    <span className="font-display text-4xl text-jungle">65000<span className="text-xl">colones</span></span>
                  </div>
                  <a href="https://wa.me/50683455135" className="btn-primary w-full text-center justify-center">
                    Agendar con SINPE
                     <img 
                      src="https://ik.imagekit.io/paraglidetour/whatsapp-50683455135-parapente-puravida.svg" 
                      alt="WhatsApp Icon"
                      className="inline-block ml-2 w-5 h-5"
                    ></img>
                  </a>
                </div>
              </div>
            </article>
          </div>
        </div>
      </section>

      <Gallery />
      <Footer />
      <WhatsAppFloat />
    </div>
  );
}

